<template>
    <flex-element
        class="f-relative"
        class-default-inline="f-w-1/1 f-object-cover"
        :class="bgClasses"
        :exclude-classes="excludedClassGroups"
        :element-type="elementType"
        data-image-atom
        @dblclick.native="openMediaLibrary"
    >
        <div class="fu-placement-fill f-z-1" :style="renderGradientStyles" />
        <!-- do not delete this <div>, it's needed for the aspect ratio option to work -->
        <div
            class="f-overflow-hidden f-h-1/1"
            :class="[classWrapper, imageContainerClasses, bgWrapperClasses]"
            data-image-wrapper
        >
            <img
                :src="renderSrc"
                :alt="renderAlt"
                class="f-w-1/1 f-h-1/1"
                :class="[classImage, imageClasses]"
                :style="filterStyle"
                data-image
            >
        </div>
    </flex-element>
</template>

<script>
import { mapState } from 'vuex';
import baseAtom from '@/mixins/base-atom/base-atom-smart';
import { AImage } from '@/data/data-models';
import { placeholderImage } from '@/utils/image';
import { TYPE_IMAGE } from '@/mixins/drawer-helper';
import { getClassDefinition } from '@/utils/tailwind-helper';
import { gradientStyleByElement, opacityStyleByElement } from '@/utils/css';

export default {
    name: 'a-image',
    mixins: [baseAtom],

    props: {
        displayName: {
            type: String,
            default: 'Image',
        },

        width: {
            type: String,
            default: '',
        },

        height: {
            type: String,
            default: '',
        },

        mediaId: {
            type: String,
            default: '00000000-0000-0000-0000-000000000000',
        },

        src: {
            type: String,
            default: '',
        },

        srcDefault: {
            type: String,
            default: placeholderImage,
        },

        alt: {
            type: String,
            default: '',
        },

        altDefault: {
            type: String,
            default: '',
        },

        classWrapper: {
            type: String,
            default: '',
        },

        classImage: {
            type: String,
            default: '',
        },

        isBackgroundImage: {
            type: Boolean,
            default: false,
        },

        templateId: {
            type: String,
            default: '{C8AB5667-46F2-44BF-8799-8BC57E65308B}',
        },

        chId: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            altNew: '',
            chIdNew: '',
            cssDisplay: 'inline',
            heightNew: '',
            isBackgroundImageNew: false,
            mediaIdNew: '',
            srcNew: '',
            widthNew: '', // TODO: check if width/height needed, if so, add to image picker
        };
    },

    computed: {
        ...mapState({
            previewMode: state => state.previewMode,
            dynamicMapPreviewMode: state => state.dynamicMapPreviewMode,
            selectedMedia: state => state.page.selectedMedia,
        }),

        filterStyle() {
            return {
                filter: this.getElementData('filter'),
            };
        },

        renderGradientStyles() {
            return {
                ...gradientStyleByElement(this),
                ...opacityStyleByElement(this),
            };
        },

        renderSrc() {
            if (!this.srcNew && this.hasElementData('touched')) return '';

            return this.srcNew || this.srcDefault;
        },

        renderAlt() {
            return this.altNew || this.altDefault;
        },

        updatedElement() {
            // force blank width/height to never save metadata
            return new AImage({
                ...this.updatedBaseElement,
                src: this.srcNew,
                alt: this.altNew,
                width: '',
                height: '',
                mediaId: this.mediaIdNew,
                isBackgroundImage: this.isBackgroundImageNew,
                chId: this.chIdNew,
            });
        },

        excludedClassGroups() {
            // using Set to strip out any duplicate classes

            return Array.from(new Set([...this.imageContainerClassGroups, ...this.imageClassGroups]));
        },

        imageContainerClassGroups() {
            return ['borderRadius'];
        },

        imageClassGroups() {
            if (this.isBackgroundImageNew) {
                return [
                    'height',
                    'objectFit',
                    'objectPosition',
                    'placement',
                    'maxHeight',
                    'maxWidth',
                    'minHeight',
                    'minWidth',
                    'transformOrigin',
                    'width',
                ];
            }

            return [
                'objectFit',
                'objectPosition',
            ];
        },

        imageContainerClasses() {
            return this.htmlClass.filter((className) => {
                const { tailwindDefinitionProp } = getClassDefinition(className);

                return this.imageContainerClassGroups.includes(tailwindDefinitionProp);
            });
        },

        imageClasses() {
            const classes = this.htmlClass.filter((className) => {
                const { tailwindDefinitionProp } = getClassDefinition(className);

                return this.imageClassGroups.includes(tailwindDefinitionProp);
            });

            if (this.isBackgroundImageNew) {
                classes.push('f-h-1/1');
            }

            return classes;
        },

        bgClasses() {
            if (!this.isBackgroundImageNew) return [];

            return ['fu-placement-fill', 'f--z-1'];
        },

        bgWrapperClasses() {
            if (!this.isBackgroundImageNew) return '';

            return ['fu-placement-fill', 'f--z-1'];
        },
    },

    watch: {
        selectedMedia() {
            if (this.selectedElements[0] === this) {
                this.srcNew = this.selectedMedia.url;
                this.mediaIdNew = this.selectedMedia.id;
                this.chIdNew = this.selectedMedia.chid ? String(this.selectedMedia.chid) : '';
                this.$emit('element-updated', this.updatedElement);
            }
        },
    },

    created() {
        if (this.isBackgroundImage) {
            this.setElementData('is-background-image', 'true', { triggerChangeDetection: false });
        }
    },

    methods: {
        setFieldValues() {
            this.srcNew = this.src;
            this.altNew = this.alt;
            this.heightNew = this.height;
            this.widthNew = this.width;
            this.mediaIdNew = this.mediaId;
            this.isBackgroundImageNew = this.isBackgroundImage || this.hasElementData('is-background-image');
            this.chIdNew = this.chId;
        },

        removeImage() {
            this.chIdNew = '';
            this.srcNew = '';
            this.mediaIdNew = '';
            this.altNew = '';
            this.heightNew = '';
            this.widthNew = '';
            this.setElementData('touched', 'true');
            this.$emit('element-updated', this.updatedElement);
        },

        openMediaLibrary() {
            if (this.previewMode) return;
            if (this.dynamicMapPreviewMode) return;

            this.openMediaDrawer(TYPE_IMAGE);
        },
    },
};
</script>
